import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import {
  describeMissingRateCard,
  RATE_CARD_ADMIN_PATH,
  type FormulaReadiness,
} from '../../utils/formulaReadiness';

/**
 * Explains a missing rate card (a $0 crew/truck rate, or a $0 / disabled
 * residential flat-rate tier) and points the viewer at the fix: admins get a
 * link to Admin → Quoting → Rate Cards, estimators are told to ask an admin.
 * Renders nothing when the rate card is not what's missing.
 */
export function RateCardHint({
  readiness,
  isOrgAdmin,
  className = '',
  inline = false,
}: {
  readiness: FormulaReadiness;
  isOrgAdmin: boolean;
  className?: string;
  /** Render only the sentence (for use inside an existing warning banner). */
  inline?: boolean;
}) {
  const reason = describeMissingRateCard(readiness);
  if (!reason) return null;
  const text = (
    <p className={`text-[12px] text-amber-800 leading-snug ${inline ? className : ''}`}>
      {reason}{' '}
      {isOrgAdmin ? (
        <>
          Set it in{' '}
          <Link to="/admin/quoting/rate-cards" className="font-medium underline hover:text-amber-900">
            {RATE_CARD_ADMIN_PATH}
          </Link>
          {readiness.isFlatRate ? ' (Residential flat rates).' : '.'}
        </>
      ) : (
        <>Ask an admin to set it in {RATE_CARD_ADMIN_PATH}.</>
      )}
    </p>
  );
  if (inline) return text;
  return (
    <div className={`flex gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-left ${className}`}>
      <AlertTriangle size={14} className="shrink-0 text-amber-600 mt-0.5" />
      {text}
    </div>
  );
}
